import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Lightbulb, Plus, Search, SearchX, SlidersHorizontal } from "lucide-react";
import notification from "../../../../helpers/notification";
import { TspBreadcrumb, TspConfirmDialog, TspEmptyState, TspSkeleton } from "../../components/primitives";
import TspLoadError from "../../components/common/TspLoadError";
import useTspPermission from "../../hooks/useTspPermission";
import useTspFlashNotice from "../../hooks/useTspFlashNotice";
import useTspJourneyLocationOptions from "../../hooks/useTspJourneyLocationOptions";
import { tspErrorMessage } from "../../hooks/useTspApiResource";
import TSP_PERMISSIONS from "../../constants/tspPermissions";
import { ADVICE_SECTIONS } from "../../constants/tspAdvice";
import { removeAdvice, useTspAdviceList } from "./useTspAdviceData";
import AdviceCard from "./components/AdviceCard";

const SEARCH_DEBOUNCE_MS = 300;
const NO_FILTERS = { search: "", journeyId: "", locationId: "", category: "" };

// Advice Repository (/tsp/advice). Filters can be preset from the URL
// (?location_id=<id>&journey=<id>&category=<key>) for deep links from other
// screens. Search, filters, category counts and paging are all done by the server.
function AdviceRepositoryContent() {
  useTspFlashNotice();
  const { hasPermission } = useTspPermission();
  const [searchParams] = useSearchParams();
  const canManage = hasPermission(TSP_PERMISSIONS.ADVICE_MANAGE);
  const canOpenKnowledge = hasPermission(TSP_PERMISSIONS.KNOWLEDGE_REPOSITORY_VIEW);

  const [searchInput, setSearchInput] = useState("");
  const [phoneFiltersOpen, setPhoneFiltersOpen] = useState(false);
  const [filters, setFilters] = useState(() => ({
    ...NO_FILTERS,
    category: searchParams.get("category") || "",
    locationId: searchParams.get("location_id") || "",
    journeyId: searchParams.get("journey") || "",
  }));
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const advice = useTspAdviceList(filters);
  const options = useTspJourneyLocationOptions();

  useEffect(() => {
    const timer = setTimeout(() => setFilters((previous) => (previous.search === searchInput ? previous : { ...previous, search: searchInput })), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const setFilter = (key, value) => setFilters((previous) => ({ ...previous, [key]: value }));
  const clearFilters = () => {
    setSearchInput("");
    setFilters(NO_FILTERS);
  };
  const hasActiveFilters = Object.values(filters).some(Boolean);
  const locationOptions = !filters.locationId || options.locations.some((entry) => String(entry.id) === filters.locationId) ? options.locations : [{ id: filters.locationId, label: "Selected city" }, ...options.locations];

  const handleDeleteConfirmed = async () => {
    setDeleting(true);
    try {
      await removeAdvice(deleteTarget.id);
      notification("success", "Advice deleted.");
      setDeleteTarget(null);
      advice.reload();
    } catch (error) {
      notification("error", tspErrorMessage(error, "Could not delete this advice. Please try again."));
    } finally {
      setDeleting(false);
    }
  };

  // Only the very first load (or a first load that failed) replaces the whole
  // page, so typing in the search box is never interrupted by a results reload.
  const firstLoad = !advice.meta;

  const heading = (
    <div className="tsp-page-heading-row">
      <div>
        <h1>Advice Repository</h1>
        <TspBreadcrumb items={["Dashboard", "Advice Repository"]} />
      </div>
      {canManage && !(firstLoad && (advice.loading || advice.error)) && (
        <Link to="/tsp/advice/new" className="tsp-btn tsp-btn-success tsp-btn-pill">
          <Plus size={15} /> Add Advice
        </Link>
      )}
    </div>
  );

  if (firstLoad && advice.loading) {
    return (
      <div className="tsp-page tsp-advice-page">
        {heading}
        <TspSkeleton variant="row" count={5} label="Loading advice" />
      </div>
    );
  }
  if (firstLoad && advice.error) {
    return (
      <div className="tsp-page tsp-advice-page">
        {heading}
        <TspLoadError error={advice.error} onRetry={advice.reload} what="the advice repository" />
      </div>
    );
  }

  const nothingAtAll = !advice.loading && !advice.error && !hasActiveFilters && advice.counts.all === 0;

  return (
    <div className="tsp-page tsp-advice-page">
      {heading}

      {nothingAtAll ? (
        <TspEmptyState
          icon={Lightbulb}
          title="No advice has been recorded yet"
          message="Lessons learned on the road — best areas, mistakes, challenges and ideas — will be collected here."
          action={
            canManage && (
              <Link to="/tsp/advice/new" className="tsp-btn tsp-btn-success">
                <Plus size={15} /> Add Advice
              </Link>
            )
          }
        />
      ) : (
        <>
          <div className={`tsp-journey-filters ${phoneFiltersOpen ? "tsp-phone-filters-open" : ""}`} role="search">
            <div className="tsp-phone-filter-bar">
              <label className="tsp-journey-search">
                <Search size={16} aria-hidden="true" />
                <input type="search" placeholder="Search advice text or city…" aria-label="Search advice" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} />
              </label>
              <button
                type="button"
                className={`tsp-phone-filter-toggle ${Boolean(filters.locationId || filters.journeyId) ? "has-active" : ""} ${phoneFiltersOpen ? "is-open" : ""}`}
                onClick={() => setPhoneFiltersOpen((open) => !open)}
                aria-expanded={phoneFiltersOpen}
                aria-label="Toggle filters"
              >
                <SlidersHorizontal size={15} />
                <span>Filters</span>
                {Boolean(filters.locationId || filters.journeyId) && <span className="tsp-phone-filter-badge">1</span>}
              </button>
            </div>
            <div className="tsp-journey-filter-body">
              <div className="tsp-journey-filter-grid">
                <select className="tsp-select tsp-filter-select" aria-label="Filter by city" value={filters.locationId} onChange={(event) => setFilter("locationId", event.target.value)}>
                  <option value="">All cities</option>
                  {locationOptions.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.label}
                    </option>
                  ))}
                </select>
                <select className="tsp-select tsp-filter-select" aria-label="Filter by journey" value={filters.journeyId} onChange={(event) => setFilter("journeyId", event.target.value)}>
                  <option value="">All journeys</option>
                  {options.journeys.map((journey) => (
                    <option key={journey.id} value={journey.id}>
                      {journey.journey_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="tsp-chip-filter-row" role="group" aria-label="Filter by category">
            <button type="button" className="tsp-chip-filter" aria-pressed={filters.category === ""} onClick={() => setFilter("category", "")}>
              All <span>{advice.counts.all}</span>
            </button>
            {ADVICE_SECTIONS.map((section) => (
              <button
                key={section.key}
                type="button"
                className={`tsp-chip-filter tsp-chip-filter-${section.tone}`}
                aria-pressed={filters.category === section.key}
                onClick={() => setFilter("category", filters.category === section.key ? "" : section.key)}
              >
                {section.label} <span>{advice.counts[section.key]}</span>
              </button>
            ))}
          </div>

          <div className="tsp-results-bar">
            <span className="tsp-muted-text" aria-live="polite">
              Showing {advice.items.length} of {advice.meta ? advice.meta.total : advice.items.length} {advice.meta?.total === 1 ? "piece of advice" : "pieces of advice"}
            </span>
            {hasActiveFilters && (
              <button type="button" className="tsp-filter-clear" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>

          {advice.loading ? (
            <TspSkeleton variant="row" count={5} label="Loading advice" />
          ) : advice.error ? (
            <TspLoadError error={advice.error} onRetry={advice.reload} what="the advice repository" />
          ) : advice.items.length === 0 ? (
            <TspEmptyState
              icon={SearchX}
              title={filters.locationId && !filters.search && !filters.journeyId && !filters.category ? "No advice has been recorded for this city yet." : "No advice matches your filters"}
              message="Try a different search or category, or clear the filters."
              action={
                <button type="button" className="tsp-btn tsp-btn-outline-primary" onClick={clearFilters}>
                  Clear filters
                </button>
              }
            />
          ) : (
            <>
              <div className="tsp-advice-grid">
                {advice.items.map((entry) => (
                  <AdviceCard key={entry.id} item={entry} canManage={canManage} canOpenKnowledge={canOpenKnowledge} onDelete={setDeleteTarget} />
                ))}
              </div>
              {advice.remaining > 0 && (
                <div className="tsp-load-more">
                  <button type="button" className="tsp-btn tsp-btn-outline-primary" onClick={advice.loadMore} disabled={advice.loadingMore}>
                    {advice.loadingMore ? "Loading…" : `Load more (${advice.remaining} remaining)`}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      <TspConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete this advice?"
        message={deleteTarget ? `This advice for ${deleteTarget.location?.label || "the city"} will be permanently deleted.` : ""}
        confirmLabel="Delete"
        busy={deleting}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

// The toast container lives outside the page's loading/error/loaded branches so it is
// never remounted (which would discard a toast queued by useTspFlashNotice), and it is
// rendered first so it is subscribed before the content's effects fire.
export default function TspAdviceRepositoryPage() {
  return (
    <>
      <ToastContainer />
      <AdviceRepositoryContent />
    </>
  );
}
